<template>
  <div class="m-3" style="height: 90vh" :dir="$ctx.getDir()">
    <div class="grid grid-cols-12 items-center content-center gap-2">
      <template v-if="$can('read', '/inventory/purchaseorders')">
        <el-select class="input-label col-span-12 md:col-span-2"
                   v-model="selectedValue">
          <el-option
              key="quickView"
              :label="$t('views.common.listView.quickView')"
              value="quickView"
          >
          </el-option>
          <el-option key="allItems"
                     :label="$t('views.common.listView.allItems')"
                     value="allItems">
          </el-option>
          <el-option
              key="export"
              :label="$t('views.common.listView.selectedRows.export')"
              value="export"
          >
          </el-option>
        </el-select>
      </template>

      <el-input
          :placeholder="t('searchReceipt')"
          class="w-full col-span-12 md:col-span-7"
          :prefix-icon="Search"
          v-model="searchTerm"
          ref="searchBar"
      />
      <div class="col-span-12 md:col-span-3 flex items-center gap-4">
        <ToggleGroup v-model="orderTypeFilter" class="gap-2 flex-wrap lg:flex-nowrap" type="multiple">
          <ToggleGroupItem value="true" class="data-[state=on]:text-red-600 bg-transparent p-1">
            <Icon icon="solar:calendar-outline" width="24" height="24" />
          </ToggleGroupItem>
        </ToggleGroup>
        <horizontal-button
            :title="t('newPurchaseOrder')"
            @click.prevent="newPurchaseOrder()"
        />
      </div>
    </div>

    <ag-grid-vue
        v-if="rowModelType"
        class="ag-theme-alpine mt-4 md:mt-0"
        :columnDefs="columnDefs"
        rowSelection="multiple"
        :enable-rtl="$ctx.getDir() === 'rtl'"
        style="height: 80vh"
        @cell-double-clicked="onCellDoubleClicked"
        :defaultColDef="defaultColDef"
        :rowData="rowData"
        :pagination="selectedValue == 'allItems'"
        :paginationAutoPageSize="selectedValue == 'allItems'"
        :onGridReady="onGridReady"
        :components="components"
        :rowBuffer="rowBuffer"
        :rowModelType="rowModelType"
        :cacheBlockSize="cacheBlockSize"
        :cacheOverflowSize="cacheOverflowSize"
        :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
        :infiniteInitialRowCount="infiniteInitialRowCount"
        :maxBlocksInCache="maxBlocksInCache"
    >
    </ag-grid-vue>

  </div>
</template>

<script lang="ts">
import {defineComponent, onBeforeMount, ref} from 'vue'
import {Icon} from '@iconify/vue';

// components
import {AgGridVue} from 'ag-grid-vue3'
import {
  ColDef,
  GridApi,
  GridOptions,
  IDatasource,
  IGetRowsParams,
  ModuleRegistry,
  ValueFormatterParams
} from 'ag-grid-community'

import {i18nOrderMessages} from '@/models/sales/Order'
import {
  OrdersPaginated,
  PurchaseOrder as Order
} from '@/models/inventory/PurchaseOrder'
import {useI18n} from 'vue-i18n'
import {formatPostgresDate, parseDate} from '@/utils/date'
import HorizontalButton from '@/components/HorizontalButton.vue'
import {Search} from '@element-plus/icons-vue'

import {InfiniteRowModelModule} from '@ag-grid-community/infinite-row-model'
import {ToggleGroup, ToggleGroupItem} from "@/components/ui/toggle-group";

ModuleRegistry.registerModules([InfiniteRowModelModule])

export default defineComponent({
  name: 'orders',

  setup() {
    const {t} = useI18n({
      messages: {
        en: {
          ...i18nOrderMessages.en,
          searchReceipt: 'Enter receipt number',
          newPurchaseOrder: 'New Purchase Order',
          normal: "Regular",
          special: "Customer’s Order",
          notes: "Notes",
          manualNumber: 'Manual No',
          spoDueDate: 'Due Date',
        },
        ar: {
          ...i18nOrderMessages.ar,
          searchReceipt: 'ادخل رقم الفاتورة',
          newPurchaseOrder: 'طلب شراء جديد',
          normal: "اعتيادي",
          special: "طلب زبون",
          notes: "الملاحظات",
          manualNumber: 'الرقم الخاص',
          spoDueDate: 'التاريخ المتوقع',
        }
      }
    })

    const rowBuffer = ref<any>(null)
    const rowSelection = ref<any>(null)
    const rowModelType = ref<any>(null)
    const cacheBlockSize = ref<any>(null)
    const cacheOverflowSize = ref<any>(null)
    const maxConcurrentDatasourceRequests = ref<any>(null)
    const infiniteInitialRowCount = ref<any>(null)
    const maxBlocksInCache = ref<any>(null)
    const orderTypeFilter = ref<Array<string>>([])

    onBeforeMount(() => {
      rowBuffer.value = 0
      rowSelection.value = 'multiple'
      rowModelType.value = 'infinite'
      cacheBlockSize.value = 100
      cacheOverflowSize.value = 100
      maxConcurrentDatasourceRequests.value = 2
      infiniteInitialRowCount.value = 0
      maxBlocksInCache.value = 10
    })

    return {
      t,
      Search,
      rowBuffer,
      rowSelection,
      rowModelType,
      cacheBlockSize,
      cacheOverflowSize,
      maxConcurrentDatasourceRequests,
      infiniteInitialRowCount,
      maxBlocksInCache,
      orderTypeFilter
    }
  },

  data() {
    const columnDefs: ColDef[] = []
    const rowData: Order[] = []
    const gridOptions: GridOptions = {}
    const defaultColDef: ColDef = {}
    const datasource: IDatasource = {} as IDatasource
    const components = {
      loadingRenderer: (params: ValueFormatterParams) => {
        return params.value !== undefined
            ? params.value
            : '<img src="https://www.ag-grid.com/example-assets/loading.gif">'
      }
    }

    return {
      dateRange: '',
      columnDefs,
      rowData,
      gridOptions,
      defaultColDef,
      components,
      datasource,
      searchTerm: '',
      selectedValue: 'quickView',
      gridApi: undefined as GridApi | undefined,
    }
  },

  components: {
    ToggleGroup,
    ToggleGroupItem,
    AgGridVue,
    HorizontalButton,
    Icon
  },

  async beforeMount() {
    this.defaultColDef = {
      resizable: true,
      minWidth: 50,
      sortable: false,
      filter: false
    }

    this.columnDefs = [
      {
        field: '-',
        hide: true,
        lockVisible: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          newRowsAction: 'keep'
        }
      },

      {
        headerName: this.t('#'),
        field: 'externalID',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: 140
      },

      {
        headerName: this.$t('models.common.createdAt'),
        field: 'createdAt',
        cellStyle: {direction: 'ltr'},
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''
          if (order.createdAt) {
            const createdAt = new Date(order.createdAt)
            return formatPostgresDate(String(createdAt), 'DD/MM/YYYY hh:mm A')
          }
          return ''
        },
        width: 180
      },

      {
        headerName: this.t('type'),
        field: 'purchaseType',
        width: 160,
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''

          return this.t(order.purchaseType)
        }
      },

      {
        headerName: this.t('orderNumber'),
        field: 'referenceableExternalID',
        width: 140
      },

      {
        headerName: this.t('manualNumber'),
        field: 'manualNumber',
        width: 140
      },

      {
        headerName: this.t('spoDueDate'),
        field: 'dueDate',
        cellStyle: ({value}) => {
          const base = {color: 'black'}
          if (!value) return base;

          if (parseDate(value) < new Date()) {
            base['color'] = 'red'
          }
          return base
        },
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''
          if (order.dueDate) {
            const createdAt = new Date(order.dueDate)
            return formatPostgresDate(String(createdAt), 'DD/MM/YYYY')
          }
          return ''
        },
        width: 140
      },

      {
        headerName: this.t('vendor'),
        field: 'vendor.name',
        cellStyle: {textAlign: 'center'}
      },
      {
        headerName: this.t('customer'),
        field: 'customer.name',
        cellStyle: {textAlign: 'center'}
      },
      {
        headerName: this.t('user'),
        field: 'user.fullName',
        cellStyle: {textAlign: 'center'}
      },

      {
        headerName: this.t('notes'),
        field: 'notes',
        cellStyle: {textAlign: 'center'}
      },

      {
        headerName: this.t('location'),
        field: 'location.enName',
        cellStyle: {textAlign: 'center'},
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return '';
          if (this.$ctx.locale == 'ar') {
            return order.location?.arName;
          }
          return order.location?.enName;
        }
      },

    ]

  },

  mounted() {
    // this.gridApi?.sizeColumnsToFit()
  },

  watch: {
    orderTypeFilter: {
      handler() {
        this.updateOrderStatusFilter();
      },
    },
    searchTerm() {
      this.filterItems()
    },
    async selectedValue(newValue, oldValue) {
      if (oldValue == 'delete' || oldValue == 'export') {
        return
      }

      if (this.selectedValue === 'delete' || this.selectedValue === 'export') {
        const selected = this.gridApi?.getSelectedRows()

        if (selected?.length) {
          if (this.selectedValue === 'delete') {
            // send request to archive
            // await this.delete(selected)
          } else {
            this.gridApi?.exportDataAsCsv({onlySelected: true})
            this.$alertModal.showSuccess({
              title: this.$t('views.common.listView.selectedRows.exportSuccess')
            })
          }

          // deselect
          selected.length = 0
        }
        this.selectedValue = oldValue
      } else if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      }
    }
  },

  methods: {
    updateOrderStatusFilter() {
      if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      }
    },
    async updateRowData() {
      this.gridApi?.showLoadingOverlay()

      const urlParams = new URLSearchParams()
      if (this.orderTypeFilter.length) {
        urlParams.set('overdue', this.orderTypeFilter.join(','))
      }

      try {
        const data = await this.$http.get<Order[]>(Order.ENDPOINT + `?${urlParams.toString()}`)
        this.rowData = data.map((order) => Order.from(order))
        this.gridApi?.setGridOption('rowData', this.rowData)
      } catch (error) {
        this.$alertModal.showDanger({
          title: error.title,
          body: error.body
        })
      }
      this.gridApi?.hideOverlay()
    },

    getDatasource() {
      const dataSource = {
        rowCount: undefined,
        getRows: async (params: IGetRowsParams) => {
          this.gridApi?.showLoadingOverlay()

          const urlParams = new URLSearchParams()
          if (this.orderTypeFilter.length) {
            urlParams.set('overdue', this.orderTypeFilter.join(','))
          }

          let url = `${Order.ENDPOINT}?startRow=${params.startRow}&endRow=${params.endRow}&${urlParams.toString()}`
          if (params.sortModel.length) {
            url += `&colId=${params.sortModel[0].colId}&sort=${params.sortModel[0].sort}`
          }

          if (Object.keys(params.filterModel).length) {
            url += `&query=${params.filterModel['-'].filter}`
          }

          const res = await this.$http.get<OrdersPaginated>(url)
          this.rowData = res.data.map((order) => Order.from(order))
          this.gridApi?.hideOverlay()

          const lastRow = res.total
          // call the success callback
          params.successCallback(this.rowData, lastRow)
        }
      }
      return dataSource
    },

    async onGridReady(params: any) {
      this.gridApi = params.api
      if (this.selectedValue == 'quickView') {
        this.gridApi?.setGridOption('datasource', this.getDatasource())
        this.gridApi?.setGridOption('serverSideSortOnServer', true)
      } else if (this.selectedValue == 'allItems') {
        this.gridApi?.setGridOption('serverSideSortOnServer', false)
        this.updateRowData()
      }
    },

    filterItems() {
      if (this.rowModelType == 'infinite') {
        this.gridApi?.setFilterModel({'-': {filter: this.searchTerm}})
        return
      }
      this.gridApi?.setQuickFilter(this.searchTerm)
    },
    onCellDoubleClicked(event: any) {
      this.$router.push(`/inventory/purchase-orders/order/${event.data.id}`)
    },
    newPurchaseOrder() {
      this.$router.push(this.$Route.INVENTORY_NEW_PURCHASE_ORDER);
    }
  }
})
</script>

<style></style>
